import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  ButtonBase,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Fade,
  Grid,
  Paper,
  Popover,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import LyricsIcon from "@mui/icons-material/Lyrics";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import BoltIcon from "@mui/icons-material/Bolt";
import InfoIcon from "@mui/icons-material/Info";
import { useTranslation } from "react-i18next";
import { SoundEngineList, useSound } from "../../../../../context/juno/SoundContext";
import { useJunoModels } from "../../../../../hooks/useJunoModels";
import { Images } from "../../../../../assets";
import { RowBox } from "../../../../../utils/styledBox";

const SoundModelSelector: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { aiEngine, setSoundEngine, setCategory } = useSound();
  const { getSoundModels } = useJunoModels();
  const soundModels = getSoundModels;

  const [selectedEngine, setSelectedEngine] = useState<SoundEngineList | null>(aiEngine);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [rotation, setRotation] = useState(0);
  const isMounted = useRef(false);
  const open = Boolean(anchorEl);

  useEffect(() => {
    setSelectedEngine(aiEngine);
  }, [aiEngine]);

  useEffect(() => {
    if (isMounted.current) {
      setRotation((prev) => prev + 360);
    } else {
      isMounted.current = true;
    }
  }, [selectedEngine]);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleModelChange = (value: string) => {
    const engineValue = value as SoundEngineList;
    setSoundEngine(engineValue);
    setSelectedEngine(engineValue);

    // カテゴリを設定
    const selectedModel = soundModels.find((model) => model.value === engineValue);
    if (selectedModel) {
      const categoryTypes = selectedModel.category.map((cat) => {
        if (cat === "Music") return "music";
        if (cat === "Sound Effects") return "sfx";
        if (cat === "Lyrics") return "lyric";
        return cat.toLowerCase() as "music" | "sfx" | "lyric";
      });
      setCategory(categoryTypes);
    }

    handlePopoverClose();
  };

  // モデルに対応したアイコンを取得
  const getCategoryIcons = (category: string[]) => {
    return (
      <RowBox gap={1}>
        {category.includes("music") && (
          <Tooltip title={t("juno.sound.category.music")} arrow placement="top">
            <MusicNoteIcon
              sx={{
                opacity: 0.8,
                color: "#fff",
                backgroundColor: "#3273a8",
                borderRadius: "50%",
                p: 0.5,
              }}
            />
          </Tooltip>
        )}
        {category.includes("sfx") && (
          <Tooltip title={t("juno.sound.category.sfx")} arrow placement="top">
            <GraphicEqIcon
              sx={{
                opacity: 0.8,
                color: "#fff",
                backgroundColor: "#036c03",
                borderRadius: "50%",
                p: 0.5,
              }}
            />
          </Tooltip>
        )}
        {category.includes("lyric") && (
          <Tooltip title={t("juno.sound.category.lyric")} arrow placement="top">
            <LyricsIcon
              sx={{
                opacity: 0.8,
                color: "#fff",
                backgroundColor: "#ce6e00",
                borderRadius: "50%",
                p: 0.5,
              }}
            />
          </Tooltip>
        )}
        {category.includes("reference") && (
          <Tooltip title={t("juno.sound.category.reference")} arrow placement="top">
            <AudioFileIcon
              sx={{
                opacity: 0.8,
                color: "#fff",
                backgroundColor: "#4d2464",
                borderRadius: "50%",
                p: 0.5,
              }}
            />
          </Tooltip>
        )}
      </RowBox>
    );
  };

  // モデルに対応した画像を取得（仮画像として画像生成系の画像を使用）
  const getModelImage = (modelValue: string) => {
    switch (modelValue) {
      case "CASSETTEAI":
        return Images.juno.aiEngine.CassetteAI;
      case "CASSETTEAI_SFX":
        return Images.juno.aiEngine.CassetteAIsfx;
      case "DIFFRHYTHM":
        return Images.juno.aiEngine.DiffRythm; // DiffRhythmが正しいが、ファイル名はDiffRythm.png
      case "ELEVENLABS":
        return Images.juno.aiEngine.ElevenLabs;
      case "MINIMAX":
        return Images.juno.aiEngine.MinimaxAudio;
      case "MMAUDIO":
        return Images.juno.aiEngine.MmAudio;
      default:
        return Images.juno.aiEngine.t2i;
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: (theme) => alpha(theme.palette.background.custom1 || theme.palette.background.paper, 0.5),
        backdropFilter: "blur(10px)",
        mb: 2,
      }}
    >
      <Box
        sx={{
          justifyContent: "center",
          display: "flex",
          alignItems: "center",
          width: "100%",
          flexDirection: "column",
        }}
      >
        <ButtonBase onClick={handlePopoverOpen} sx={{ width: "100%", borderRadius: 1, overflow: "hidden" }}>
          <Box sx={{ position: "relative", width: "100%", height: "100px" }}>
            <Fade in={true} key={aiEngine} timeout={1000}>
              <CardMedia
                component="img"
                image={aiEngine ? getModelImage(aiEngine) : Images.juno.aiEngine.t2i}
                alt={aiEngine || ""}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  filter: "brightness(0.6)",
                }}
              />
            </Fade>
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                p: 2,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ width: "100%", textAlign: "center" }}>
                <Typography variant="body2" color="white">
                  {t("juno.sound.ai.model")}
                </Typography>
                {selectedEngine && (
                  <Fade in={true} key={selectedEngine} timeout={1000}>
                    <Box sx={{ mt: 1 }}>
                      <RowBox sx={{ gap: 1, justifyContent: "center" }}>
                        <BoltIcon
                          sx={{
                            color: "white",
                            transform: `rotate(${rotation}deg)`,
                            transition: "transform 1s ease-in-out",
                          }}
                        />
                        <Typography variant="body2" whiteSpace="nowrap" color="white" fontWeight="bold">
                          {soundModels.find((model) => model.value === selectedEngine)?.label || selectedEngine}
                        </Typography>
                      </RowBox>
                    </Box>
                  </Fade>
                )}
              </Box>
              <Box sx={{ alignSelf: "flex-end" }}>
                {selectedEngine && (
                  <RowBox gap={1}>
                    {getCategoryIcons(soundModels.find((model) => model.value === selectedEngine)?.category || [])}
                  </RowBox>
                )}
              </Box>
            </Box>
          </Box>
        </ButtonBase>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          elevation={4}
          sx={{ mx: 1 }}
          slotProps={{
            paper: {
              sx: {
                backgroundColor: (theme) =>
                  alpha(theme.palette.background.custom1 || theme.palette.background.paper, 0.5),
                backdropFilter: "blur(15px)",
              },
              elevation: 0,
            },
          }}
        >
          <Box sx={{ p: 2, width: { xs: "100vw", md: 800 } }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t("juno.sound.ai.model")}
            </Typography>
            <Grid container spacing={2}>
              {soundModels.map((option, index) => (
                <Grid key={index} size={{ xs: 6 }}>
                  <Fade in={true} timeout={200 * index > 1000 ? 1000 : 200 * index}>
                    <Card
                      sx={{
                        width: "100%",
                        border: selectedEngine === option.value ? "2px solid" : "0px solid",
                        borderColor: selectedEngine === option.value ? theme.palette.primary.main : "transparent",
                      }}
                      onClick={() => handleModelChange(option.value)}
                      elevation={0}
                    >
                      <CardActionArea>
                        <CardContent
                          sx={{
                            p: 0,
                            position: "relative",
                            height: 120,
                          }}
                        >
                          <CardMedia
                            component="img"
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              filter: "brightness(0.5)",
                            }}
                            image={getModelImage(option.value)}
                            alt={option.label}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              p: 2,
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box>
                              <Typography variant="body1" fontWeight="bold" color="white">
                                {option.label}
                              </Typography>
                              <Typography variant="caption" color="white" sx={{ opacity: 0.8 }}>
                                {option.description}
                              </Typography>
                            </Box>
                            <Box sx={{ alignSelf: "flex-end" }}>
                              <RowBox gap={1}>
                                {getCategoryIcons(option.category)}
                                <Tooltip title={option.description} arrow placement="top">
                                  <InfoIcon
                                    sx={{
                                      opacity: 0.8,
                                      color: "#fff",
                                      backgroundColor: "#313131",
                                      borderRadius: "50%",
                                      p: 0.5,
                                    }}
                                  />
                                </Tooltip>
                              </RowBox>
                            </Box>
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Popover>
      </Box>
    </Paper>
  );
};

export default SoundModelSelector;
